import type { Ref } from 'react';
import { forwardRef } from 'react';
import type { InputBaseProps, InputRenderProps } from './InputBase';
import InputBase from './InputBase';

export type NumberInputProps = Pick<InputBaseProps, 'label' | 'onChange' | 'size' | 'isRequired' | 'isDisabled' | 'testId' > & {
  /**
   * Value here should be a number instead of a string.
   */
  value: number;
  /**
   * Minimum value allowed in the number field
   */
  min?: number;
  /**
   * Maximum value allowed in the number field
   */
  max?: number;
  /**
   * Value by which to increment inputs
   */
  step?: number;
};

/**
 * A generic, controlled number input for FOSSA Form elements.
 */
const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(({
  label,
  value,
  onChange,
  size = 'medium',
  isRequired,
  isDisabled,
  testId,
  min,
  max,
  step,
}: NumberInputProps, ref: Ref<HTMLInputElement>) => (
  <InputBase
    type="number"
    label={label}
    size={size}
    value={value}
    onChange={onChange}
    isRequired={isRequired}
    isDisabled={isDisabled}
    testId={testId}
  >
    {(inputBaseProps: InputRenderProps) => (
      <input
        ref={ref}
        min={min}
        max={max}
        step={step}
        style={{ textAlign: 'right' }}
        // We want to inject a large number of unknown props here, so spreading is necessary.
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...inputBaseProps}
      />
    )}
  </InputBase>
));

export default NumberInput;
